import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux'; 
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Event } from '../../../../redux/slices/eventsSlice'; // Adjust the path accordingly
import api from '../../../../services/api';

interface Worker {
    worker_id: number;
    worker_name: string;
}

interface Flow {
    flow_id: number;
    flow_name: string;
}

interface EventFileEventProps {
    show: boolean;
    onClose: () => void;
    event?: Event | null; // `event` is optional to support creating a new event
    onSave: (updatedEvent: Event) => Promise<any>; // Expect `onSave` to return a promise with the response or error
}

const EventFileEvent: React.FC<EventFileEventProps> = ({ show, onClose, event, onSave }) => {
    const [formData, setFormData] = useState<Event | null>(null);
    const [apiResponse, setApiResponse] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [workers, setWorkers] = useState<Worker[]>([]);
    const [flows, setFlows] = useState<Flow[]>([]);
    const reduxError = useSelector((state: any) => state.events.error);

    useEffect(() => {
        if (event) {
            setFormData(event); // Set form data for editing
        } else {
            // Initialize default values for creating a new event
            setFormData({
                event_id: 0, // Will be auto-generated by the backend
                name: '',
                description: '',
                type: 'fileEvent',
                action: {
                    type: 'startFlow',
                    flow_id: null, // Use this for the flow ID
                    parameters: [],
                },
                config: {
                    worker: '',
                    directory: '',
                    namingpattern: '',
                    move: false,
                    movedirectory: '',
                },
            });

        }
        setApiResponse(null); // Reset the API response when opening the modal
    }, [event]);

    // Fetch worker data from the API
    useEffect(() => {
        const fetchWorkers = async () => {
            try {
                const response = await api.get('/workers');
                const workerOptions = response.data.map((worker: any) => ({
                    worker_id: worker.worker_id,
                    worker_name: worker.worker_name,
                }));
                setWorkers(workerOptions);
            } catch (error) {
                console.error('Failed to fetch workers:', error);
            }
        };
        fetchWorkers();
    }, []);

    // Fetch flow data from the API
    useEffect(() => {
        const fetchFlows = async () => {
            try {
                const response = await api.get('/flows');
                const flowOptions = response.data.map((flow: any) => ({
                    flow_id: flow.flow_id,
                    flow_name: flow.flow_name,
                }));
                setFlows(flowOptions);
            } catch (error) {
                console.error('Failed to fetch flows:', error);
            }
        };
        fetchFlows();
    }, []);

    const handleChange = (field: string, value: any, parentField?: string) => {
        console.log("handleChange - " + field + " value: " + value + " typeof: " + typeof value)
        setFormData((prev) => {
            if (!prev) return null;
            if (parentField) {
                return {
                    ...prev,
                    [parentField]: {
                        ...prev[parentField],
                        [field]: value,
                    },
                };
            }
            return {
                ...prev,
                [field]: value,
            };
        });
        console.log("formData: " + JSON.stringify(formData))
    };

    const handleSave = async () => {
        if (formData) {
            console.log('Saving formData:', formData); // Debug data
            setIsSubmitting(true);
            try {
                await onSave(formData); // Assuming `onSave` handles the API request
                setApiResponse({ type: 'success', message: 'Event saved successfully!' });
            } catch (error: any) {
                console.error('Save failed:', error);
    
                // Extract error message from API response
                const errorMessage = error.response?.data?.error || error.message || 'Failed to save event.';
                setApiResponse({
                    type: 'error',
                    message: errorMessage,
                });
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    
    

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>{event ? 'Edit Event' : 'New Event'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Display API response */}
                {apiResponse && (
    <Alert variant={apiResponse.type === 'success' ? 'success' : 'danger'} className="mb-3">
        {apiResponse.message}
    </Alert>
)}
                <Form>
                    {/* Name Field */}
                    <Form.Group controlId="name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData?.name || ''}
                            onChange={(e) => handleChange('name', e.target.value)}
                        />
                    </Form.Group>

                    {/* Description Field */}
                    <Form.Group controlId="description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            value={formData?.description || ''}
                            onChange={(e) => handleChange('description', e.target.value)}
                        />
                    </Form.Group>

                    {/* Type Field */}
                    <Form.Group controlId="type">
                        <Form.Label>Type</Form.Label>
                        <Form.Control
                            as="select"
                            value={formData?.type || ''}
                            onChange={(e) => handleChange('type', e.target.value)}
                        >
                            <option value="fileEvent">File Event</option>
                        </Form.Control>
                    </Form.Group>

                    {formData?.type === 'fileEvent' && (
                        <>
                            {/* Worker Dropdown */}
                            <Form.Group controlId="worker">
                                <Form.Label>Worker</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={formData.config.worker || ''}
                                    onChange={(e) => handleChange('worker', e.target.value, 'config')}
                                >
                                    <option value="">Select a worker</option>
                                    {workers.map((worker) => (
                                        <option key={worker.worker_id} value={worker.worker_id}>
                                            {worker.worker_name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            {/* Directory Field */}
                            <Form.Group controlId="directory">
                                <Form.Label>Directory</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.config.directory || ''}
                                    onChange={(e) => handleChange('directory', e.target.value, 'config')}
                                />
                            </Form.Group>

                            {/* Naming Pattern Field */}
                            <Form.Group controlId="namingpattern">
                                <Form.Label>Naming Pattern</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.config.namingpattern || ''}
                                    onChange={(e) => handleChange('namingpattern', e.target.value, 'config')}
                                />
                            </Form.Group>

                            {/* Move Checkbox */}
                            <Form.Group controlId="move">
                                <Form.Check
                                    type="checkbox"
                                    label="Move"
                                    checked={formData.config.move || false}
                                    onChange={(e) => handleChange('move', e.target.checked, 'config')}
                                />
                            </Form.Group>

                            {/* Move Directory Field */}
                            {formData.config.move && (
                                <Form.Group controlId="movedirectory">
                                    <Form.Label>Move Directory</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.config.movedirectory || ''}
                                        onChange={(e) => handleChange('movedirectory', e.target.value, 'config')}
                                    />
                                </Form.Group>
                            )}

                            {/* Action Type Field */}
                            <Form.Group controlId="actionType">
                                <Form.Label>Action Type</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={formData.action.type || ''}
                                    onChange={(e) => handleChange('type', e.target.value, 'action')}
                                >
                                    <option value="startFlow">Start Flow</option>
                                </Form.Control>
                            </Form.Group>

                            {/* Action Name Dropdown */}
                            <Form.Group controlId="actionName">
                                <Form.Label>Action Name</Form.Label>
                                <Form.Control
    as="select"
    value={formData.action.flow_id || ''}
    onChange={(e) => handleChange('flow_id', parseInt(e.target.value, 10) || null, 'action')} // Convert to number
>
    <option value="">Select a flow</option>
    {flows.map((flow) => (
        <option key={flow.flow_id} value={flow.flow_id}>
            {flow.flow_name}
        </option>
    ))}
</Form.Control>

                            </Form.Group>

                            {/* Parameters Section */}
                            <Form.Group controlId="parameters">
                                <Form.Label>Parameters</Form.Label>
                                <div>
                                    {formData.action.parameters.map((param, index) => (
                                        <div key={index} className="d-flex mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Name"
                                                value={param.name || ''}
                                                onChange={(e) =>
                                                    handleChange('parameters', [
                                                        ...formData.action.parameters.slice(0, index),
                                                        { ...param, name: e.target.value },
                                                        ...formData.action.parameters.slice(index + 1),
                                                    ], 'action')
                                                }
                                                className="me-2"
                                            />
                                            <Form.Control
                                                type="text"
                                                placeholder="Value"
                                                value={param.value || ''}
                                                onChange={(e) =>
                                                    handleChange('parameters', [
                                                        ...formData.action.parameters.slice(0, index),
                                                        { ...param, value: e.target.value },
                                                        ...formData.action.parameters.slice(index + 1),
                                                    ], 'action')
                                                }
                                            />
                                            <Button
                                                variant="danger"
                                                onClick={() =>
                                                    handleChange(
                                                        'parameters',
                                                        formData.action.parameters.filter((_, i) => i !== index),
                                                        'action'
                                                    )
                                                }
                                                className="ms-2"
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    variant="secondary"
                                    className="mt-2"
                                    onClick={() =>
                                        handleChange(
                                            'parameters',
                                            [...formData.action.parameters, { name: '', value: '' }],
                                            'action'
                                        )
                                    }
                                >
                                    Add Parameter
                                </Button>
                            </Form.Group>
                        </>
                    )}
                </Form>

            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
                    {event ? 'Save Changes' : 'Create Event'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EventFileEvent;
